// src/modules/auth/hooks/use-auth.ts
'use client';

import { useState, useCallback, useEffect } from 'react';
import { authenticateUser } from '../services/auth-service';
import { useAuthStore } from '../stores/auth-store';
import { AuthUser } from '../types/auth';
import { Either, isRight } from '@shared-commons/utils';
import { Auth } from 'firebase/auth';

export const useAuth = () => {
  const [authState, setAuthState] = useState<{
    loading: boolean;
    error: string | null;
    authenticated: boolean;
    user: AuthUser | null;
  }>({
    loading: false,
    error: null,
    authenticated: false,
    user: null,
  });

  const { user, setUser, clearAuth } = useAuthStore();

  useEffect(() => {
    if (user && user.token) {
      setAuthState({
        loading: true,
        error: null,
        authenticated: false,
        user: null, // Proprietà mancante
      });
    } else {
      setAuthState({
        loading: false,
        error: null,
        authenticated: false,
        user: null,
      });
    }
  }, [user]);

  const authenticate = useCallback(
    async (credentials: { email: string; password: string }, auth: Auth) => {
      setAuthState({
        loading: true,
        error: null,
        authenticated: false,
        user: null,
      });
      const result: Either<Error, AuthUser> = await authenticateUser(
        credentials.email,
        credentials.password,
        auth,
      )();
      if (isRight(result)) {
        const user = result.value;
        if (user && user.token) {
          setUser(user);
          setAuthState({
            loading: false,
            error: null,
            authenticated: true,
            user,
          });
        } else {
          setAuthState({
            loading: false,
            error: 'Invalid user or token',
            authenticated: false,
            user: null,
          });
        }
      } else {
        setAuthState({
          loading: false,
          error: result.value.message,
          authenticated: false,
          user: null,
        });
      }
    },
    [setUser],
  );

  const logout = useCallback(() => {
    clearAuth();
    setAuthState({
      loading: false,
      error: null,
      authenticated: false,
      user: null,
    });
  }, [clearAuth]);

  return { authState, authenticate, logout };
};
